import { InvitePlayerDialog } from "./-components/invite-player-dialog"
import { ScheduleQuizDialog } from "./-components/schedule-quiz-dialog"
import { createScheduledQuizzesColumns } from "./-components/scheduled-quizzes-columns"
import { createTeamMembersColumns } from "./-components/team-members-columns"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable } from "@/components/ui/data-table"
import { Skeleton } from "@/components/ui/skeleton"
import { formatDateInLocalTimezone } from "@/lib/timezone-utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Text } from "@/components/ui/text"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { isCustomConvexError } from "@convex/utils"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Calendar, Trophy, Users } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

export const Route = createFileRoute("/(app)/app/teams/$teamId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { teamId } = Route.useParams()
  const navigate = useNavigate()
  const [enteringQuizId, setEnteringQuizId] = useState<string | null>(null)

  const currentUser = useQuery(api.users.handlers.current)
  const team = useQuery(api.teams.handlers.getTeam, {
    teamId: teamId as Id<"teams">,
  })
  const teamMembers = useQuery(api.teams.handlers.getTeamMembers, {
    teamId: teamId as Id<"teams">,
  })
  const scheduledQuizzes = useQuery(
    api.scheduledQuizzes.handlers.getScheduledQuizzesForTeam,
    { teamId: teamId as Id<"teams"> }
  )
  const teamStats = useQuery(api.quizzes.handlers.getTeamStats, {
    teamId: teamId as Id<"teams">,
  })

  const startScheduledQuiz = useMutation(
    api.quizState.handlers.startScheduledQuiz
  )

  const isCaptain = Boolean(
    currentUser && team && team.captainId === currentUser._id
  )

  const handleEnterQuiz = (scheduledQuizId: Id<"scheduledQuizzes">) => {
    void (async () => {
      try {
        setEnteringQuizId(scheduledQuizId)
        const quizStateId = await startScheduledQuiz({
          scheduledQuizId,
        })

        await navigate({
          to: "/app/quizzes/$quizStateId",
          params: { quizStateId },
        })
      } catch (error) {
        if (isCustomConvexError(error)) {
          toast.error(error.data.message)
        } else {
          toast.error("Failed to enter quiz. Please try again.")
        }
        setEnteringQuizId(null)
      }
    })()
  }

  if (!currentUser || !team || !teamMembers || scheduledQuizzes === undefined) {
    return (
      <div className="container mx-auto py-8">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-[400px]" />
      </div>
    )
  }

  // Filter out any scheduled quizzes with null quiz data
  const validScheduledQuizzes = scheduledQuizzes.filter(
    (sq) => sq.quiz !== null
  )

  // Prepare scheduled quizzes columns
  const scheduledQuizzesColumns = createScheduledQuizzesColumns({
    onEnter: handleEnterQuiz,
    isCaptain,
    enteringQuizId,
  })

  // Prepare team members columns
  const teamMembersColumns = createTeamMembersColumns(
    teamId as Id<"teams">,
    currentUser._id,
    team.captainId
  )

  // Determine if this is a "pairs" type team
  const isPair = team.type === "pairs"
  const entityLabel = isPair ? "Pair" : "Team"

  return (
    <div className="container mx-auto py-8">
      {/* Team Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold">{team.name}</h1>
          <Badge variant="outline">{entityLabel}</Badge>
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-6">
        {/* Scheduled Quizzes Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Scheduled Quizzes
              </CardTitle>
              {isCaptain && (
                <ScheduleQuizDialog teamId={teamId as Id<"teams">} />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {validScheduledQuizzes.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No scheduled quizzes yet.</p>
                {isCaptain && (
                  <p className="text-sm mt-2">
                    Click "Schedule Quiz" to get started!
                  </p>
                )}
              </div>
            ) : (
              <DataTable
                columns={scheduledQuizzesColumns}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                data={validScheduledQuizzes as any}
                filterColumn="quiz.name"
                filterPlaceholder="Search quizzes..."
              />
            )}
          </CardContent>
        </Card>

        {/* Team Members Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {entityLabel} Members
              </CardTitle>
              {isCaptain && (
                <InvitePlayerDialog teamId={teamId as Id<"teams">} />
              )}
            </div>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={teamMembersColumns}
              data={teamMembers}
              filterColumn="name"
              filterPlaceholder="Search members..."
            />
          </CardContent>
        </Card>

        {/* Team Stats Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              {entityLabel} Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {teamStats === undefined ? (
              <Skeleton className="h-[200px] w-full" />
            ) : teamStats === null || teamStats.results.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No completed games yet.</p>
                <p className="text-sm mt-2">
                  Complete some quizzes to see your {entityLabel.toLowerCase()}{" "}
                  stats here!
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Text className="text-3xl font-bold">
                      {teamStats.totalGames}
                    </Text>
                    <Text className="text-sm text-muted-foreground">
                      Games Played
                    </Text>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Text className="text-3xl font-bold">
                      {teamStats.totalScore}
                    </Text>
                    <Text className="text-sm text-muted-foreground">
                      Total Score
                    </Text>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Text className="text-3xl font-bold">
                      {teamStats.avgScore.toFixed(1)}
                    </Text>
                    <Text className="text-sm text-muted-foreground">
                      Avg per Game
                    </Text>
                  </div>
                </div>

                {/* Results Table */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Game</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Score</TableHead>
                      <TableHead className="text-right">Place</TableHead>
                      <TableHead className="w-[120px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teamStats.results.slice(0, 10).map((result) => (
                      <TableRow key={result.quizId}>
                        <TableCell className="font-medium">
                          {result.quizName}
                        </TableCell>
                        <TableCell>
                          {formatDateInLocalTimezone(result.startAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">
                            {result.score}/{result.totalQuestions}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {result.resultsPublished && result.rank !== null ? (
                            <Badge
                              variant={result.rank <= 3 ? "default" : "outline"}
                              className={
                                result.rank === 1
                                  ? "bg-yellow-500"
                                  : result.rank === 2
                                    ? "bg-gray-400"
                                    : result.rank === 3
                                      ? "bg-amber-600"
                                      : ""
                              }
                            >
                              #{result.rank}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {result.resultsPublished ? "—" : "Pending"}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {result.resultsPublished ? (
                            <Button size="sm" asChild>
                              <Link
                                to="/app/teams/$teamId/quiz/$quizId"
                                params={{
                                  teamId: teamId,
                                  quizId: result.quizId,
                                }}
                              >
                                View Details
                              </Link>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              —
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
